import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, SimpleChanges } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';

export interface InfoTooltip {
  message: string;
  link?: InfoTooltipLink;
}

interface InfoTooltipLink {
  href: string;
  target?: string;
  text?: string;
}

@Component({
  selector: 'info-tooltip',
  templateUrl: './info-tooltip.component.html',
  styleUrls: ['./info-tooltip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InfoTooltipComponent {
  @Input() tooltip?: InfoTooltip | string;

  readonly creationTrigger = '@CreateNewContent';
  tooltipMessage: string | null = null;

  private textFieldElement: Element | null = null;
  private tooltipContentWrapperElement: HTMLDivElement | null = null;

  constructor(private ref: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tooltip'].currentValue) {
      this.checkTooltipType(changes['tooltip'].currentValue);
    }
  }

  ngAfterViewInit(): void {
    this.textFieldElement = this.getTextFieldElement();
    this.cdr.detectChanges();
  }

  private getTextFieldElement(): Element | null {
    return this.ref.nativeElement.parentElement?.getElementsByClassName('mat-mdc-text-field-wrapper').item(0) ?? null;
  }

  get tooltipWrapperHeight(): string {
    let height: number | null = null;

    if (this.textFieldElement) {
      height = this.textFieldElement.clientHeight;
    }

    return height === null ? 'auto' : `${height}px`;
  }

  private checkTooltipType(tooltip: Readonly<InfoTooltip> | string): void {
    if (typeof tooltip === 'string') {
      this.tooltipMessage = tooltip;
    } else {
      this.createNewTooltipContent(tooltip);
    }
  }

  private createNewTooltipContent(tooltip: Readonly<InfoTooltip>): void {
    this.tooltipContentWrapperElement = document.createElement('div');
    const tooltipMessageElement = document.createElement('span');

    this.tooltipContentWrapperElement.setAttribute('style', 'display:flex; flex-flow:column; row-gap:5px; white-space: pre-line;');

    tooltipMessageElement.appendChild(document.createTextNode(tooltip.message.trim()));

    this.tooltipContentWrapperElement.appendChild(tooltipMessageElement);

    if (tooltip.link) {
      const tooltipLinkElement = document.createElement('a');

      tooltipLinkElement.setAttribute('href', tooltip.link.href);
      tooltipLinkElement.setAttribute('style', 'color:khaki');
      tooltipLinkElement.setAttribute('target', tooltip.link.target ?? '_blank');
      tooltipLinkElement.appendChild(document.createTextNode(tooltip.link.text ?? 'Подробнее...'));

      this.tooltipContentWrapperElement.appendChild(tooltipLinkElement);
    }
  }

  showTooltip(tooltip: MatTooltip): void {
    tooltip.show();

    const overlayRef = tooltip._overlayRef;
    const message = tooltip.message;

    if (overlayRef && message === this.creationTrigger) {
      const tooltipSurfaceElemet = overlayRef.hostElement.getElementsByClassName('mdc-tooltip__surface').item(0);

      tooltipSurfaceElemet?.childNodes.forEach((node) => {
        if (node.nodeType === node.TEXT_NODE) {
          node.remove();
        }

        if (this.tooltipContentWrapperElement) {
          tooltipSurfaceElemet.appendChild(this.tooltipContentWrapperElement);
        }
      });
    }
  }
}
